/**
 * ServerProfilePage — V2 orchestrator for the server profile detail page.
 *
 * Replaces the ~2,000-line V1 Server.jsx monolith. Manages:
 *   - Session & permissions
 *   - Three polling cycles (basic data 600s, realtime status 5s, match terms 600s)
 *   - Tab state (basic, database, match terms, logs, troubleshooting)
 *   - Drawer/dialog state for all edit flows
 *   - Toast notifications
 *
 * Props:
 *   id — Server device ID (from URL param)
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './server-profile-page.css';
import NavigationV2 from '../../../components/v2/NavigationV2.jsx';
import ProfileBanner from './components/ProfileBanner.jsx';
import BasicInfoTab from './components/BasicInfoTab.jsx';
import BasicInfoDrawer from './components/BasicInfoDrawer.jsx';
import DatabaseTab from './components/DatabaseTab.jsx';
import DatabaseConfigDrawer from './components/DatabaseConfigDrawer.jsx';
import TroubleshootingTab from './components/TroubleshootingTab.jsx';
import MatchTermsTab from './components/MatchTermsTab.jsx';
import MatchTermDrawer from './components/MatchTermDrawer.jsx';
import ServerLogsTab from './components/ServerLogsTab.jsx';

import {
  getSession, getCampusId, getUserId, getSessionId,
  getCampusList, getCampusConfig, updateSessionCampus, getRoleWeight,
} from '../../../services/session.js';
import {
  fetchServerDevice, fetchServerAreas, fetchServerStatusHistory,
  fetchServerLogs, fetchServerConfig, fetchServerMqStatus, fetchLocalAlerts,
  fetchMatchTerms, fetchVirtualDevices, fetchVirtualDeviceTypes,
  fetchEventCategories, fetchEventTypes,
  editServerBasicInfo, saveServerConfig,
  addMatchTerms, editMatchTerm, deleteMatchTerms,
  addVirtualDevices, deleteVirtualDevices,
  pingDatabase, validateDatabase, fixDatabase, interrogateAlerts,
} from './api/serverProfileApi.js';
import { iAmHigherAuthority as iAmHigherAuth, convertIsoToLocal } from '../../../libs.jsx';

// ==========================================================================
// COMPONENT
// ==========================================================================

const ServerProfilePage = ({ id }) => {
  const navigate = useNavigate();

  // --------------------------------------------------------------------------
  // SESSION
  // --------------------------------------------------------------------------
  const [campusId, setCampusId] = useState(() => getCampusId());
  const campusList = useMemo(() => getCampusList() || [], []);
  const campusName = useMemo(() => {
    const s = getSession();
    return s?.campusData?.[campusId.toString()]?.name || s?.campus || '';
  }, [campusId]);
  const isCloud = useMemo(() => !!getCampusConfig()?.is_cloud, [campusId]);
  const myRoleWeight = useMemo(() => getRoleWeight(), []);
  const editRights = useMemo(() => iAmHigherAuth(10, myRoleWeight), [myRoleWeight]);

  const serverId = useMemo(() => (id ? parseInt(id, 10) : 0), [id]);

  const getSessionParams = useCallback(() => ({
    campusId,
    userId: getUserId(),
    clientId: getSessionId(),
  }), [campusId]);

  // --------------------------------------------------------------------------
  // TOAST
  // --------------------------------------------------------------------------
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);

  const showToast = useCallback((type, message) => {
    clearTimeout(toastTimerRef.current);
    setToast({ type, message });
    toastTimerRef.current = setTimeout(() => setToast(null), 6000);
  }, []);

  useEffect(() => () => clearTimeout(toastTimerRef.current), []);

  // --------------------------------------------------------------------------
  // TABS
  // --------------------------------------------------------------------------
  const [activeTab, setActiveTab] = useState('basic');

  // --------------------------------------------------------------------------
  // CORE STATE
  // --------------------------------------------------------------------------
  const [loading, setLoading] = useState(true);

  // Basic data extracted from sp_dev_getPalcareDevicesByCampusID
  const [serverName, setServerName] = useState('');
  const [macAddr, setMacAddr] = useState('');
  const [product, setProduct] = useState('');
  const [productId, setProductId] = useState('');
  const [areaId, setAreaId] = useState('');
  const [areaName, setAreaName] = useState('');
  const [isActive, setIsActive] = useState(0);
  const [interfaceName, setInterfaceName] = useState('');
  const [interfaceId, setInterfaceId] = useState(0);
  const [os, setOs] = useState('');
  const [hardware, setHardware] = useState('');
  const [ipAddr, setIpAddr] = useState('');
  const [isOffline, setIsOffline] = useState(null);

  // Areas dropdown
  const [areas, setAreas] = useState([]);

  // Device status history
  const [deviceHistory, setDeviceHistory] = useState([]);

  // Server config (core_config rows)
  const [configData, setConfigData] = useState([]);

  // MQ status
  const [mqData, setMqData] = useState([]);
  const [uptime, setUptime] = useState('');
  const [lastReboot, setLastReboot] = useState('');
  const [activeAlarmsCount, setActiveAlarmsCount] = useState('');
  const [recentAlarmTime, setRecentAlarmTime] = useState('');
  const [lastHeartbeat, setLastHeartbeat] = useState('');

  // Local alerts
  const [localAlerts, setLocalAlerts] = useState([]);

  // DB validation
  const [dbValid, setDbValid] = useState(null);

  // Server logs (cloud only)
  const [logData, setLogData] = useState([]);

  // Match terms & virtual devices
  const [matchTermData, setMatchTermData] = useState([]);
  const [virtualDevices, setVirtualDevices] = useState([]);
  const [virtualDeviceTypes, setVirtualDeviceTypes] = useState([]);
  const [eventCategories, setEventCategories] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);

  // --------------------------------------------------------------------------
  // DRAWER / DIALOG STATE
  // --------------------------------------------------------------------------
  const [basicDrawerOpen, setBasicDrawerOpen] = useState(false);
  const [dbConfigDrawerOpen, setDbConfigDrawerOpen] = useState(false);
  const [matchDrawerOpen, setMatchDrawerOpen] = useState(false);
  const [matchDrawerMode, setMatchDrawerMode] = useState('add'); // 'add' | 'edit'
  const [matchDrawerItem, setMatchDrawerItem] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteDialogType, setDeleteDialogType] = useState(''); // 'matchterms' | 'virtualdevices'
  const [deleteItems, setDeleteItems] = useState([]);
  const [addVirtualDialogOpen, setAddVirtualDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // --------------------------------------------------------------------------
  // POLLING COUNTERS (mirrored from V1)
  // --------------------------------------------------------------------------
  const countRef = useRef(0);   // Primary: basic data (600s)
  const pCountRef = useRef(0);  // Secondary: realtime status (5s)
  const iCountRef = useRef(0);  // Tertiary: match terms (600s, conditional)

  // --------------------------------------------------------------------------
  // POLLING CYCLE 1: Basic data (600 second interval)
  // --------------------------------------------------------------------------
  const fetchBasicData = useCallback(async () => {
    const [basicResult, areasResult, historyResult] = await Promise.all([
      fetchServerDevice(campusId, serverId),
      fetchServerAreas(campusId),
      fetchServerStatusHistory(campusId, serverId),
    ]);

    // Process basic data
    if (basicResult?.length > 0) {
      const b = basicResult[0];
      setServerName(b.description || '');
      setMacAddr(b.mac_addr || '');
      setProduct(b.product || '');
      setProductId(b.palcare_product_id || '');
      setAreaId(b.area_id || '');
      setAreaName(b.area_name || '');
      setInterfaceName(b.palcare_interface_name || '');
      setInterfaceId(b.palcare_interface_id || 0);
      setOs(b.os || '');
      setHardware(b.hardware || '');
      setIpAddr(b.ip_address || '');
      setIsActive(b.is_active ? 1 : 0);
      if (isCloud) {
        setLastHeartbeat(b.last_heard || '');
        setIsOffline(b.is_offline);
      }
    }

    setAreas(areasResult || []);
    setDeviceHistory(historyResult || []);

    // Cloud-only: fetch logs
    if (isCloud) {
      const logsResult = await fetchServerLogs(serverId);
      setLogData(logsResult || []);
    }

    setLoading(false);
  }, [campusId, serverId, isCloud]);

  useEffect(() => {
    // Fire immediately, then every 600 seconds
    fetchBasicData();
    const interval = setInterval(fetchBasicData, 600_000);
    return () => clearInterval(interval);
  }, [fetchBasicData]);

  // --------------------------------------------------------------------------
  // POLLING CYCLE 2: Realtime status (5 second interval, requires macAddr)
  // --------------------------------------------------------------------------
  const fetchRealtimeStatus = useCallback(async () => {
    if (!macAddr) return;

    const mac = macAddr;
    const [alertsResult, configResult, mqResult] = await Promise.all([
      fetchLocalAlerts(mac),
      fetchServerConfig(mac),
      fetchServerMqStatus(mac),
    ]);

    setLocalAlerts(alertsResult || []);
    setConfigData(configResult || []);
    setMqData(mqResult || []);

    // Process MQ data
    if (mqResult?.length > 0) {
      const mq = mqResult[0];
      setRecentAlarmTime(mq.recent_alarm_input_time !== undefined ? convertIsoToLocal(mq.recent_alarm_input_time) : '');
      setActiveAlarmsCount(mq.count_active_alarms !== undefined ? mq.count_active_alarms : '');
      setLastReboot(mq.last_reboot_date !== undefined ? convertIsoToLocal(mq.last_reboot_date) : '');
      setUptime(mq.uptime_hrs !== undefined ? mq.uptime_hrs : '');
      if (!isCloud) {
        setLastHeartbeat(mq.create_time !== undefined ? convertIsoToLocal(mq.create_time) : '');
      }
    }
  }, [macAddr, isCloud]);

  useEffect(() => {
    if (!macAddr) return;
    fetchRealtimeStatus();
    const interval = setInterval(fetchRealtimeStatus, 5_000);
    return () => clearInterval(interval);
  }, [fetchRealtimeStatus]);

  // --------------------------------------------------------------------------
  // POLLING CYCLE 3: Match terms (600s, only if interfaceId is 1 or 2)
  // --------------------------------------------------------------------------
  const fetchMatchData = useCallback(async () => {
    if (![1, 2].includes(interfaceId)) return;

    const [termsResult, vdResult, vdTypesResult, catResult, typeResult] = await Promise.all([
      fetchMatchTerms(campusId, serverId),
      fetchVirtualDevices(campusId),
      fetchVirtualDeviceTypes(campusId),
      fetchEventCategories(campusId),
      fetchEventTypes(campusId),
    ]);

    setMatchTermData(termsResult || []);
    setVirtualDevices(vdResult || []);
    setVirtualDeviceTypes(vdTypesResult || []);
    setEventCategories(catResult || []);
    setEventTypes(typeResult || []);
  }, [campusId, serverId, interfaceId]);

  useEffect(() => {
    if (![1, 2].includes(interfaceId)) return;
    fetchMatchData();
    const interval = setInterval(fetchMatchData, 600_000);
    return () => clearInterval(interval);
  }, [fetchMatchData]);

  // --------------------------------------------------------------------------
  // DB PING — fires once when macAddr becomes available
  // --------------------------------------------------------------------------
  useEffect(() => {
    if (!macAddr) return;
    pingDatabase(macAddr, campusId).then((data) => {
      setDbValid(data?.length > 0 ? data[0] : {});
    });
  }, [macAddr, campusId]);

  // --------------------------------------------------------------------------
  // CAMPUS CHANGE HANDLER
  // --------------------------------------------------------------------------
  const handleCampusChange = useCallback((e) => {
    const val = parseInt(e.target.value, 10);
    updateSessionCampus(val);
    // Server is campus-specific — navigate back to server list for the new campus
    navigate('/community/server_devices');
  }, [navigate]);

  // --------------------------------------------------------------------------
  // ACTION HANDLERS
  // --------------------------------------------------------------------------

  /** Save basic info (name, interface, area, status) */
  const handleBasicInfoSave = useCallback(async (formData) => {
    setSaving(true);
    const [ok, errMsg] = await editServerBasicInfo({
      area_id: parseInt(formData.areaId, 10),
      description: formData.description,
      mac_addr: macAddr,
      config_id: formData.configId || 0,
      palcare_interface_id: parseInt(formData.interfaceId, 10),
      interface_change: parseInt(formData.interfaceId, 10) !== interfaceId,
      palcare_device_id: serverId,
      name_change: formData.description !== serverName,
      is_active: formData.isActive,
    }, getSessionParams());
    setSaving(false);

    if (ok) {
      showToast('success', 'Server info updated successfully.');
      setBasicDrawerOpen(false);
      fetchBasicData();
    } else {
      showToast('error', errMsg || 'Failed to update server info.');
    }
  }, [macAddr, interfaceId, serverId, serverName, getSessionParams, showToast, fetchBasicData]);

  /** Save database config */
  const handleDbConfigSave = useCallback(async (configItems) => {
    setSaving(true);
    const [ok, errMsg] = await saveServerConfig(configItems, getSessionParams());
    setSaving(false);

    if (ok) {
      showToast('success', 'Database configuration saved.');
      setDbConfigDrawerOpen(false);
      fetchRealtimeStatus();
    } else {
      showToast('error', errMsg || 'Failed to save database configuration.');
    }
  }, [getSessionParams, showToast, fetchRealtimeStatus]);

  /** Validate database */
  const handleDbValidate = useCallback(async () => {
    const data = await validateDatabase(macAddr, campusId, interfaceId);
    return data;
  }, [macAddr, campusId, interfaceId]);

  /** Fix database */
  const handleDbFix = useCallback(async (missingConfigs, extraConfigs) => {
    await fixDatabase(macAddr, campusId, interfaceId, missingConfigs, extraConfigs);
    // Re-validate after fix
    const data = await validateDatabase(macAddr, campusId, interfaceId);
    showToast('success', 'Database fix applied.');
    return data;
  }, [macAddr, campusId, interfaceId, showToast]);

  /** Interrogate alerts (MQTT) */
  const handleInterrogate = useCallback(async () => {
    const [ok] = await interrogateAlerts(campusId, getSessionId(), macAddr);
    if (ok) {
      showToast('success', 'Alert interrogation sent. Refreshing...');
      // Trigger a realtime refresh after a brief delay
      setTimeout(fetchRealtimeStatus, 2000);
    } else {
      showToast('error', 'Alert interrogation failed.');
    }
  }, [campusId, macAddr, showToast, fetchRealtimeStatus]);

  /** Add match terms */
  const handleAddMatchTerms = useCallback(async (terms) => {
    setSaving(true);
    const [ok, errMsg] = await addMatchTerms(terms, getSessionParams());
    setSaving(false);

    if (ok) {
      showToast('success', 'Match term(s) added.');
      setMatchDrawerOpen(false);
      fetchMatchData();
    } else {
      showToast('error', errMsg || 'Failed to add match term(s).');
    }
  }, [getSessionParams, showToast, fetchMatchData]);

  /** Edit a match term */
  const handleEditMatchTerm = useCallback(async (term) => {
    setSaving(true);
    const [ok, errMsg] = await editMatchTerm(term, getSessionParams());
    setSaving(false);

    if (ok) {
      showToast('success', 'Match term updated.');
      setMatchDrawerOpen(false);
      fetchMatchData();
    } else {
      showToast('error', errMsg || 'Failed to update match term.');
    }
  }, [getSessionParams, showToast, fetchMatchData]);

  /** Delete match terms (bulk) */
  const handleDeleteMatchTerms = useCallback(async (items) => {
    setSaving(true);
    const userId = getUserId();
    const payload = items.map((item) => ({
      matchterm_id: item.matchterm_id,
      modified_by: userId,
    }));
    const [ok, errMsg] = await deleteMatchTerms(payload, getSessionParams());
    setSaving(false);

    if (ok) {
      showToast('success', `${items.length} match term(s) deleted.`);
      setDeleteDialogOpen(false);
      setDeleteItems([]);
      fetchMatchData();
    } else {
      showToast('error', errMsg || 'Failed to delete match terms.');
    }
  }, [getSessionParams, showToast, fetchMatchData]);

  /** Add virtual devices */
  const handleAddVirtualDevices = useCallback(async (devices) => {
    setSaving(true);
    const [ok, errMsg] = await addVirtualDevices(devices, getSessionParams());
    setSaving(false);

    if (ok) {
      showToast('success', 'Virtual device(s) added.');
      setAddVirtualDialogOpen(false);
      fetchMatchData();
    } else {
      showToast('error', errMsg || 'Failed to add virtual device(s).');
    }
  }, [getSessionParams, showToast, fetchMatchData]);

  /** Delete virtual devices (bulk) */
  const handleDeleteVirtualDevices = useCallback(async (items) => {
    setSaving(true);
    const userId = getUserId();
    const payload = items.map((item) => ({
      device_id: item.device_id,
      modified_by: userId,
    }));
    const [ok, errMsg] = await deleteVirtualDevices(payload, getSessionParams());
    setSaving(false);

    if (ok) {
      showToast('success', `${items.length} virtual device(s) deleted.`);
      setDeleteDialogOpen(false);
      setDeleteItems([]);
      fetchMatchData();
    } else {
      showToast('error', errMsg || 'Failed to delete virtual devices.');
    }
  }, [getSessionParams, showToast, fetchMatchData]);

  // --------------------------------------------------------------------------
  // DRAWER / DIALOG OPENERS
  // --------------------------------------------------------------------------
  const openMatchDrawerAdd = useCallback(() => {
    setMatchDrawerMode('add');
    setMatchDrawerItem(null);
    setMatchDrawerOpen(true);
  }, []);

  const openMatchDrawerEdit = useCallback((item) => {
    setMatchDrawerMode('edit');
    setMatchDrawerItem(item);
    setMatchDrawerOpen(true);
  }, []);

  const openDeleteDialog = useCallback((type, items) => {
    setDeleteDialogType(type);
    setDeleteItems(items);
    setDeleteDialogOpen(true);
  }, []);

  // --------------------------------------------------------------------------
  // DERIVED VALUES
  // --------------------------------------------------------------------------
  const showMatchTermsTab = [1, 2].includes(interfaceId);
  const showLogsTab = isCloud;

  const statusDot = isOffline === true ? 'offline'
    : isOffline === false ? 'online'
    : (mqData?.length > 0 ? 'online' : null);

  const statusLabel = isOffline === true ? 'Offline'
    : isOffline === false ? 'Online'
    : (mqData?.length > 0 ? 'Online' : null);

  const bannerMeta = useMemo(() => {
    const fields = [
      { label: 'Interface', value: interfaceName },
      { label: 'Product', value: product },
      { label: 'IP Address', value: ipAddr },
    ];
    return fields;
  }, [interfaceName, product, ipAddr]);

  // --------------------------------------------------------------------------
  // TAB LIST
  // --------------------------------------------------------------------------
  const tabs = useMemo(() => {
    const t = [
      { key: 'basic', label: 'Basic Info' },
      { key: 'database', label: 'Database' },
    ];
    if (showMatchTermsTab) {
      t.push({ key: 'match', label: 'Match Terms' });
    }
    if (showLogsTab) {
      t.push({ key: 'logs', label: 'Server Logs' });
    }
    t.push({ key: 'debug', label: 'Troubleshooting' });
    return t;
  }, [showMatchTermsTab, showLogsTab]);

  // --------------------------------------------------------------------------
  // RENDER
  // --------------------------------------------------------------------------
  return (
    <>
      <NavigationV2 />
      <div className="nav2-main">
      <div className="dp-page">
        {/* Toast */}
        {toast && (
          <div className={`dp-toast dp-toast--${toast.type}`}>
            <span>{toast.message}</span>
            <button className="dp-toast__close" onClick={() => setToast(null)}>✕</button>
          </div>
        )}

        {/* Page Header */}
        <header className="dp-page-header">
          <div className="dp-page-header__inner">
            <div className="dp-breadcrumb-row">
              <nav className="dp-breadcrumb">
                <Link to="/community/server_devices" className="dp-breadcrumb__text">Servers</Link>
                <span className="dp-breadcrumb__separator">/</span>
                <span className="dp-breadcrumb__current">{serverName || 'Server Profile'}</span>
              </nav>
              <div className="dp-campus-row">
                <label htmlFor="sp-campus-selector" className="dp-campus-label">Campus:</label>
                {campusList.length > 1 ? (
                  <select
                    id="sp-campus-selector"
                    className="dp-campus-select"
                    value={campusId}
                    onChange={handleCampusChange}
                  >
                    {campusList.map((c) => (
                      <option key={c.campus_id} value={c.campus_id}>{c.campus_name}</option>
                    ))}
                  </select>
                ) : (
                  <span className="dp-campus-name">{campusName}</span>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Profile Banner */}
        <ProfileBanner
          name={serverName}
          subtitle={macAddr}
          statusDot={statusDot}
          statusLabel={statusLabel}
          metaFields={bannerMeta}
          loading={loading}
        />

        {/* Tab Bar */}
        <div className="pp-tab-bar">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              className={`pp-tab${activeTab === tab.key ? ' is-active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="pp-tab-content">
          {activeTab === 'basic' && (
            <BasicInfoTab
              serverName={serverName}
              macAddr={macAddr}
              interfaceName={interfaceName}
              product={product}
              areaName={areaName}
              os={os}
              hardware={hardware}
              ipAddr={ipAddr}
              isActive={isActive}
              lastHeartbeat={lastHeartbeat}
              recentAlarmTime={recentAlarmTime}
              activeAlarmsCount={activeAlarmsCount}
              lastReboot={lastReboot}
              uptime={uptime}
              isOffline={isOffline}
              isCloud={isCloud}
              loading={loading}
              editRights={editRights}
              onEdit={() => setBasicDrawerOpen(true)}
            />
          )}

          {activeTab === 'database' && (
            <DatabaseTab
              configData={configData}
              dbValid={dbValid}
              editRights={editRights}
              onEditConfig={() => setDbConfigDrawerOpen(true)}
              onValidate={handleDbValidate}
              onFix={handleDbFix}
              loading={loading}
            />
          )}

          {activeTab === 'match' && showMatchTermsTab && (
            <MatchTermsTab
              matchTermData={matchTermData}
              virtualDevices={virtualDevices}
              virtualDeviceTypes={virtualDeviceTypes}
              editRights={editRights}
              onAddMatchTerm={openMatchDrawerAdd}
              onEditMatchTerm={openMatchDrawerEdit}
              onDeleteMatchTerms={(items) => openDeleteDialog('matchterms', items)}
              onAddVirtualDevice={() => setAddVirtualDialogOpen(true)}
              onDeleteVirtualDevices={(items) => openDeleteDialog('virtualdevices', items)}
              campusId={campusId}
              serverId={serverId}
              userId={getUserId()}
            />
          )}

          {activeTab === 'logs' && showLogsTab && (
            <ServerLogsTab logData={logData} />
          )}

          {activeTab === 'debug' && (
            <TroubleshootingTab
              mqData={mqData}
              localAlerts={localAlerts}
              interfaceId={interfaceId}
              onInterrogate={handleInterrogate}
              editRights={editRights}
            />
          )}
        </div>

        {/* ---- Drawers ---- */}

        {/* Basic Info Drawer */}
        {basicDrawerOpen ? (
          <div className="dp-drawer-backdrop" onClick={() => setBasicDrawerOpen(false)} aria-hidden="true" />
        ) : null}
        <BasicInfoDrawer
          open={basicDrawerOpen}
          onClose={() => setBasicDrawerOpen(false)}
          onSave={handleBasicInfoSave}
          saving={saving}
          serverName={serverName}
          interfaceId={interfaceId}
          areaId={areaId}
          isActive={isActive}
          areas={areas}
        />

        {/* Database Config Drawer */}
        {dbConfigDrawerOpen ? (
          <div className="dp-drawer-backdrop" onClick={() => setDbConfigDrawerOpen(false)} aria-hidden="true" />
        ) : null}
        <DatabaseConfigDrawer
          open={dbConfigDrawerOpen}
          onClose={() => setDbConfigDrawerOpen(false)}
          onSave={handleDbConfigSave}
          saving={saving}
          configData={configData}
          userId={getUserId()}
        />

        {/* Match Term Drawer */}
        {matchDrawerOpen ? (
          <div className="dp-drawer-backdrop" onClick={() => setMatchDrawerOpen(false)} aria-hidden="true" />
        ) : null}
        <MatchTermDrawer
          open={matchDrawerOpen}
          onClose={() => setMatchDrawerOpen(false)}
          mode={matchDrawerMode}
          item={matchDrawerItem}
          onAdd={handleAddMatchTerms}
          onEdit={handleEditMatchTerm}
          saving={saving}
          areas={areas}
          virtualDevices={virtualDevices}
          eventCategories={eventCategories}
          eventTypes={eventTypes}
          campusId={campusId}
          serverId={serverId}
          userId={getUserId()}
        />

        {/* Delete Confirmation Dialog */}
        {deleteDialogOpen && (
          <div className="pp-dialog-backdrop" onClick={() => setDeleteDialogOpen(false)}>
            <div className="pp-dialog" onClick={(e) => e.stopPropagation()}>
              <div className="pp-dialog__header">
                <h2 className="pp-dialog__title">
                  Confirm Delete
                </h2>
                <button className="dp-drawer__close" onClick={() => setDeleteDialogOpen(false)}>✕</button>
              </div>
              <div className="pp-dialog__body">
                <p className="pp-dialog__text">
                  Are you sure you want to delete {deleteItems.length}{' '}
                  {deleteDialogType === 'matchterms' ? 'match term' : 'virtual device'}
                  {deleteItems.length !== 1 ? 's' : ''}? This action cannot be undone.
                </p>
              </div>
              <div className="pp-dialog__footer">
                <button
                  className="dp-btn dp-btn--cancel"
                  onClick={() => setDeleteDialogOpen(false)}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  className="dp-btn dp-btn--danger"
                  onClick={() => {
                    if (deleteDialogType === 'matchterms') {
                      handleDeleteMatchTerms(deleteItems);
                    } else {
                      handleDeleteVirtualDevices(deleteItems);
                    }
                  }}
                  disabled={saving}
                >
                  {saving ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add Virtual Device Dialog */}
        {addVirtualDialogOpen && (
          <div className="pp-dialog-backdrop" onClick={() => setAddVirtualDialogOpen(false)}>
            <div className="pp-dialog" onClick={(e) => e.stopPropagation()}>
              <div className="pp-dialog__header">
                <h2 className="pp-dialog__title">Add Virtual Device</h2>
                <button className="dp-drawer__close" onClick={() => setAddVirtualDialogOpen(false)}>✕</button>
              </div>
              <div className="pp-dialog__body">
                {virtualDeviceTypes.length === 0 ? (
                  <p className="pp-dialog__text">No virtual device types available.</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {virtualDeviceTypes.map((vdt) => (
                      <label key={vdt.device_type_id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: 'var(--font-size-sm)', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          className="dp-checkbox"
                          data-typeid={vdt.device_type_id}
                          data-typename={vdt.device_type_name}
                        />
                        {vdt.device_type_name}
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <div className="pp-dialog__footer">
                <button
                  className="dp-btn dp-btn--cancel"
                  onClick={() => setAddVirtualDialogOpen(false)}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  className="dp-btn dp-btn--primary"
                  disabled={saving}
                  onClick={() => {
                    const checked = document.querySelectorAll('.pp-dialog input[type="checkbox"]:checked');
                    if (checked.length === 0) return;
                    const devices = Array.from(checked).map((cb) => ({
                      campus_id: campusId,
                      is_active: 1,
                      resident_id: 0,
                      area_id: 0,
                      is_area_device: 1,
                      device_name: cb.dataset.typename,
                      manufacture_id: 9,
                      descriptions: '',
                      device_type_id: parseInt(cb.dataset.typeid, 10),
                      manufacture_uuid: '',
                    }));
                    handleAddVirtualDevices(devices);
                  }}
                >
                  {saving ? 'Adding…' : 'Add Selected'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      </div>
    </>
  );
};

export default ServerProfilePage;
